<template>
    <CustomMenu
        v-model="isOpenPopup"
        hideTransitionSlider
    >
        <template #activator>
            <div
                class="font-sans-rubik flex items-center gap-x-3 cursor-pointer rounded-1.25 hover:bg-menu-purple70 dark:hover:bg-menu-blue770 transition-colors"
                :class="mini ? 'justify-center mx-2 py-2' : 'mx-2 px-2 py-2'"
                @click="onToggle"
            >
                <UserImage :image="currentUser.image" />

                <div
                    v-if="!mini"
                    class="min-w-0"
                >
                    <div class="mb-0.5 text-xs text-gray-370 dark:text-white truncate">
                        {{ userName }}
                    </div>

                    <div class="text-xxxs leading-normal tracking-0.01em text-gray-200 dark:text-gray-250 truncate">
                        {{ currentUser.email }}
                    </div>
                </div>
            </div>
        </template>

        <template #dropdown>
            <div
                class="font-sans-rubik absolute left-14 -bottom-11 w-[216px] rounded-1.25 bg-white dark:bg-blue-810 border border-white-500 dark:border-gray-760 shadow-menuWhite overflow-hidden"
                @click.stop
            >
                <div class="flex items-center gap-x-3 px-4 py-2 border-b border-white-500 dark:border-gray-760">
                    <UserImage :image="currentUser.image" />

                    <div class="min-w-0">
                        <div class="mb-0.5 text-xs font-medium text-gray-370 dark:text-white truncate">
                            {{ userName }}
                        </div>

                        <div class="text-xxxs leading-normal text-gray-250 dark:text-gray-250 truncate">
                            {{ currentUser.email }}
                        </div>
                    </div>
                </div>

                <div class="py-1 px-2">
                    <RouterLink
                        :to="settingsTo"
                        class="flex items-center gap-x-2 w-full h-9 px-2 mb-0.5 rounded-1.25 text-xs font-medium dark:font-normal text-gray-370 dark:text-white hover:text-gray-370 dark:hover:text-white hover:bg-purple-70 dark:hover:bg-blue-780 transition-colors"
                        @click="onClose"
                    >
                        <span class="w-5 flex items-center justify-center text-gray-370 dark:text-gray-200">
                            <SettingsGearIcon />
                        </span>

                        Settings
                    </RouterLink>

                    <button
                        type="button"
                        class="flex items-center gap-x-2 w-full h-9 px-2 rounded-1.25 text-xs font-medium dark:font-normal text-gray-370 dark:text-white hover:text-gray-370 dark:hover:text-white hover:bg-purple-70 dark:hover:bg-blue-780 transition-colors"
                        @click="onLogout"
                    >
                        <span class="w-5 flex items-center justify-center text-gray-370 dark:text-gray-200">
                            <LogoutIcon />
                        </span>

                        Log out
                    </button>
                </div>

                <div class="flex items-center gap-x-2.5 px-4 py-3 border-t border-white-500 dark:border-gray-760">
                    <ThemeToggle v-model:currentTheme="currentTheme" />

                    <div class="text-xxxs font-medium dark:font-normal text-gray-370 dark:text-gray-200 capitalize">
                        {{ currentTheme }} theme
                    </div>
                </div>
            </div>
        </template>
    </CustomMenu>
</template>

<script setup>
import { computed, ref } from 'vue'

import CustomMenu from '@/components/menu/CustomMenu/CustomMenu.vue'
import UserImage from '@/components/userImage/userImage.vue'
import ThemeToggle from '@/components/buttons/ThemeToggle.vue'
import SettingsGearIcon from '@/components/icons/SettingsGearIcon.vue'
import LogoutIcon from '@/components/icons/LogoutIcon.vue'

import { useAuthorizationStore } from '@/store/authorizationStore'

import { ROUTES } from '@/constants/routes'

defineProps({
    mini: {
        type: Boolean,
        default: false,
    },
})

const authorizationStore = useAuthorizationStore()

const isOpenPopup = ref(false)
const currentTheme = ref('')

const currentUser = computed(() => authorizationStore.user || {})

const userName = computed(() => {
    const { first_name, last_name } = currentUser.value

    return `${first_name || ''} ${last_name || ''}`.trim()
})

const settingsTo = computed(() => {
    if (!authorizationStore.isOwner || !authorizationStore.isWorkspaceOwner) {
        return { name: ROUTES.accountDetails }
    }

    return {
        name: ROUTES.manageWorkspace,
        params: {
            account: authorizationStore.getAccountSlug,
            workspace: authorizationStore.getWorkspaceSlug,
        },
    }
})

function onToggle() {
    isOpenPopup.value = !isOpenPopup.value
}

function onClose() {
    isOpenPopup.value = false
}

function onLogout() {
    onClose()
    authorizationStore.logout()
}
</script>
